"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.practiceLabel = void 0;
exports.inTabOrder = inTabOrder;
exports.consolidatedTable = consolidatedTable;
exports.measureColumns = measureColumns;
exports.measureCells = measureCells;
exports.summaryCells = summaryCells;
exports.chipVariant = chipVariant;
const clinician_js_1 = require("../clinician.js");
const format_js_1 = require("../format.js");
const inactive_js_1 = require("./inactive.js");
const EMPTY = "—";
const BP_MEASURE = "236";
const DX_LABEL = {
    "001": "Diabetes Dx Code",
    "236": "HTN Dx Code",
};
/** The measures in `tabs` order; any the tabs do not name come after them. */
function inTabOrder(tabs, measures) {
    const at = (m) => tabs.includes(m.measureNumber) ? tabs.indexOf(m.measureNumber) : tabs.length;
    return [...measures].sort((a, b) => at(a) - at(b));
}
/** J-262 (C4): Inactive is always Y or N; the server decides it from the dates of death and discharge. */
const inactiveCell = (inactive) => (inactive ? "Y" : "N");
/** J-274 (NR-05 a): the reason and date cells; blank when the patient is active. */
const inactiveCells = (words, r) => ({
    inactive_reason: (0, inactive_js_1.inactiveReasonWord)(words, r.inactiveReason) ?? "",
    inactive_date: r.inactiveDate ?? "",
});
/**
 * The TIN Name cell, "Name (TIN)" or "TIN (TIN)" when unnamed, on the Quality screen and in the Gap Closure
 * Workbook. J-332: a gap without a TIN (its type says it has one, J-330) shows "—", not "null (null)".
 */
const practiceLabel = (r) => r.tin === null ? EMPTY : `${r.practiceName ?? r.tin} (${r.tin})`;
exports.practiceLabel = practiceLabel;
function consolidatedTable(report, columns, tabs, words) {
    const measures = inTabOrder(tabs, report.measures);
    const shown = columns.filter((c) => c.visible);
    return {
        columns: shown.flatMap((c) => c.id === "measure_status"
            ? measures.map((m) => ({ id: `m${m.measureKey}`, header: m.shortLabel }))
            : [{ id: c.id, header: c.label }]),
        rows: report.consolidatedRows.map((r, index) => {
            const statuses = new Map(r.statuses.map((s) => [s.measureKey, s]));
            const cells = {
                practice: (0, exports.practiceLabel)(r),
                facility: r.facility ?? "",
                aco: r.acoCode,
                performance_group: r.performanceGroup ?? EMPTY,
                member_id: r.memberId ?? EMPTY,
                first_name: r.firstName,
                last_name: r.lastName,
                dob: r.dob,
                inactive: inactiveCell(r.inactive),
                ...inactiveCells(words, r),
                clinician: r.clinicians.map(clinician_js_1.clinicianLabel).join("; ") || EMPTY,
            };
            // A cell with a label is also a chip; a "—" cell stays text.
            const chips = {};
            for (const m of measures) {
                const s = statuses.get(m.measureKey);
                cells[`m${m.measureKey}`] = s?.label ?? EMPTY;
                if (s?.label != null)
                    chips[`m${m.measureKey}`] = {
                        label: s.label,
                        variant: chipVariant(s),
                        measureNumber: m.measureNumber,
                        action: s.action,
                    };
            }
            // J-373: the line's patient and ACO, for the why pop-up its chips open.
            return { id: `${r.tin}/${index}`, cells, chips, patientId: r.patientId, acoCode: r.acoCode };
        }),
    };
}
function measureColumns(measure, columns) {
    return columns
        .filter((c) => c.visible &&
        (c.id !== "bp" || measure.measureNumber === BP_MEASURE) &&
        ((c.id !== "dx_code" && c.id !== "dx_date") ||
            DX_LABEL[measure.measureNumber] !== undefined))
        .map((c) => ({
        id: c.id,
        header: c.id === "status"
            ? `${measure.shortLabel} Status`
            : c.id === "dx_code"
                ? DX_LABEL[measure.measureNumber]
                : c.label,
    }));
}
function measureCells(r, words) {
    return {
        practice: (0, exports.practiceLabel)(r),
        // J-275: blank when the facility is unknown.
        facility: r.facility ?? "",
        aco: r.acoCode,
        performance_group: r.performanceGroup ?? EMPTY,
        member_id: r.memberId ?? EMPTY,
        first_name: r.firstName,
        last_name: r.lastName,
        dob: r.dob,
        inactive: inactiveCell(r.inactive),
        ...inactiveCells(words, r),
        // J-273: "Name (NPI)", or the NPI alone when no name is known.
        clinician: r.clinician === null ? EMPTY : (0, clinician_js_1.clinicianLabel)(r.clinician),
        status: r.statusLabel,
        qualifying_encounter: r.qualifyingEncounter,
        qe_code: r.qeCode ?? EMPTY,
        qe_date: r.qeDate ?? EMPTY,
        bp: r.bp ?? EMPTY,
        exclusion_code: r.exclusionCode ?? EMPTY,
        exclusion_date: r.exclusionDate ?? EMPTY,
        numerator_code: r.numeratorCode ?? EMPTY,
        numerator_date: r.numeratorDate ?? EMPTY,
        action: r.action,
        dx_code: r.dxCode ?? EMPTY,
        dx_date: r.dxDate ?? EMPTY,
    };
}
/** The summary line's cells for one measure (CR2-062). */
function summaryCells(m) {
    return {
        measure: `${m.displayNumber} ${m.shortLabel}`,
        numerator: (0, format_js_1.formatCount)(m.numerator),
        denominator: (0, format_js_1.formatCount)(m.denominator),
        performance: (0, format_js_1.formatPerformanceRate)(m.performanceRate),
    };
}
/** The chip's colour: an open gap red, an exclusion grey, an exception amber, the rest green. */
function chipVariant(r) {
    if (r.gap)
        return "error";
    if (r.ruleKey === "excluded")
        return "cancelled";
    if (r.ruleKey === "exception")
        return "pending";
    return "complete";
}
//# sourceMappingURL=report-table.js.map